import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Button, Typography, Paper, Alert, 
  Link as MuiLink, InputAdornment, IconButton, CircularProgress,
  Fade, Grow
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon, VpnKey as OtpIcon, LockReset as ResetIcon } from '@mui/icons-material';
import api from '../../api';

const Login = () => {
  const [step, setStep] = useState(1); 
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState(null); 
  const [loading, setLoading] = useState(false); 
  const [showPassword, setShowPassword] = useState(false); 

  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const empId = localStorage.getItem('emp_id');
    
    if (token && role && empId) {
      window.location.href = "/Dashboard";
    }
  }, []);

  const handleLogin = async () => {
    if (!email || !password) {
      setMessage({ type: 'error', text: "Please enter both email and password" });
      return;
    }

    setLoading(true);
    setMessage(null);
    
    try {
      const res = await api.post('/auth/login', { email, password });
      
      if (res.data.requires_otp) {
        setStep(2); 
        setMessage({ type: 'success', text: "OTP sent to your email" });
      } else {
        // NEW: We now capture and store first_name and last_name from the backend
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('role', res.data.role);
        localStorage.setItem('emp_id', res.data.emp_id);
        localStorage.setItem('first_name', res.data.first_name);
        localStorage.setItem('last_name', res.data.last_name);
        
        window.location.href = "/Dashboard";
      }
    } catch (err) {
      let errorMessage = "Login failed";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setMessage({ type: 'error', text: "Please enter a valid 6-digit OTP" });
      return;
    }

    setLoading(true);
    setMessage(null);
    
    try {
      const res = await api.post('/auth/verify-otp', { email, otp });
      // NEW: Ensure OTP login also captures the names
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('role', res.data.role);
      localStorage.setItem('emp_id', res.data.emp_id);
      localStorage.setItem('first_name', res.data.first_name);
      localStorage.setItem('last_name', res.data.last_name);
      
      window.location.href = "/Dashboard";
    } catch (err) {
      let errorMessage = "Invalid OTP";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setMessage({ type: 'error', text: "Please enter your email address" });
      return;
    }

    if (!email.includes('@')) {
      setMessage({ type: 'error', text: "Please enter a valid email address" });
      return;
    }

    setLoading(true);
    setMessage(null);
    
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setMessage({ type: 'success', text: res.data.message || "Password reset instructions sent to your email" });
      setTimeout(() => {
        setStep(1);
        setMessage(null);
      }, 3000);
    } catch (err) {
      let errorMessage = "Failed to process request";
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        p: 2
      }}
    >
      <Grow in={true} timeout={800}>
        <Paper 
          elevation={8} 
          sx={{ 
            p: { xs: 2.5, sm: 3.5 }, 
            width: '100%',
            maxWidth: 400,
            borderRadius: 3,
            backgroundColor: 'white'
          }}
        >
          {/* Logo Section */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: -2, mt: 2 }}>
            <img 
              src="/logo_main.png" 
              alt="TransACT Logo" 
              style={{ 
                width: 'auto',
                height: '170px',
                maxWidth: '280px',
                objectFit: 'contain'
              }} 
            />
          </Box>

          <Typography 
            variant="h6" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 600,
              color: '#383534',
              mb: 1.5,
              fontSize: '1.1rem'
            }}
          >
            Welcome to TransACT People
          </Typography>
      
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ mb: 2.5, color: 'text.secondary', fontSize: '0.8rem' }}
          >
            {step === 1 && "Sign in to your account"}
            {step === 2 && "Two-Factor Authentication"}
            {step === 3 && "Password Recovery"}
          </Typography>
          
          {message && (
            <Fade in={true}>
              <Alert 
                severity={message.type} 
                sx={{ mb: 2.5, borderRadius: 1.5, fontSize: '0.8rem' }}
                onClose={() => setMessage(null)}
              >
                {message.text}
              </Alert>
            </Fade>
          )}
          
          {step === 1 && (
            <Fade in={step === 1} timeout={500}>
              <Box>
                <TextField 
                  fullWidth 
                  label="Official Email" 
                  type="email"
                  size="small"
                  sx={{ mb: 2 }}
                  onChange={e => setEmail(e.target.value)}
                  value={email}
                  disabled={loading}
                  InputProps={{
                    sx: { borderRadius: 1.5 }
                  }}
                />
                
                <TextField 
                  fullWidth 
                  label="Password" 
                  type={showPassword ? "text" : "password"}
                  size="small"
                  sx={{ mb: 2.5 }}
                  onChange={e => setPassword(e.target.value)}
                  value={password}
                  disabled={loading}
                  InputProps={{
                    sx: { borderRadius: 1.5 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                
                <Button 
                  variant="contained" 
                  size="medium" 
                  fullWidth 
                  onClick={handleLogin}
                  disabled={loading}
                  sx={{ 
                    borderRadius: 1.5,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : <><LoginIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Login</>}
                </Button>
                
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <MuiLink 
                    component="button" 
                    variant="body2" 
                    onClick={() => { setStep(3); setMessage(null); }}
                    sx={{ 
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Forgot Password?
                  </MuiLink>
                </Box>
              </Box>
            </Fade>
          )}

          {step === 2 && (
            <Fade in={step === 2} timeout={500}>
              <Box>
                <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5, fontSize: '0.8rem' }}>
                  Enter the 6-digit OTP sent to <strong>{email}</strong>
                </Alert>
                
                <TextField 
                  fullWidth 
                  label="6-Digit OTP" 
                  size="small"
                  sx={{ mb: 2.5 }}
                  onChange={e => setOtp(e.target.value)}
                  value={otp}
                  disabled={loading}
                  inputProps={{ maxLength: 6 }}
                  InputProps={{
                    sx: { borderRadius: 1.5, fontSize: '1rem', letterSpacing: 2 }
                  }}
                />
                
                <Button 
                  variant="contained" 
                  size="medium" 
                  fullWidth 
                  color="success" 
                  onClick={verifyOtp}
                  disabled={loading}
                  sx={{ 
                    borderRadius: 1.5,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    mb: 1.5
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : <><OtpIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Verify & Login</>}
                </Button>
                
                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink 
                    component="button" 
                    variant="body2" 
                    onClick={() => setStep(1)}
                    sx={{ 
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Back to Login
                  </MuiLink>
                </Box>
              </Box>
            </Fade>
          )}

          {step === 3 && (
            <Fade in={step === 3} timeout={500}>
              <Box>
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5, fontSize: '0.8rem' }}>
                  Enter your official email address to receive password reset instructions
                </Alert>
                
                <TextField 
                  fullWidth 
                  label="Official Email" 
                  type="email"
                  size="small"
                  sx={{ mb: 2.5 }}
                  onChange={e => setEmail(e.target.value)}
                  value={email}
                  disabled={loading}
                  InputProps={{
                    sx: { borderRadius: 1.5 }
                  }}
                />
                
                <Button 
                  variant="contained" 
                  size="medium" 
                  fullWidth 
                  color="warning" 
                  onClick={handleForgotPassword}
                  disabled={loading}
                  sx={{ 
                    borderRadius: 1.5,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    mb: 1.5,
                    backgroundColor: '#ed6c02',
                    '&:hover': {
                      backgroundColor: '#e65100',
                    }
                  }}
                >
                  {loading ? <CircularProgress size={20} color="inherit" /> : <><ResetIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} /> Reset Password</>}
                </Button>
                
                <Box sx={{ textAlign: 'center' }}>
                  <MuiLink 
                    component="button" 
                    variant="body2" 
                    onClick={() => setStep(1)}
                    sx={{ 
                      color: '#1976d2',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Back to Login
                  </MuiLink>
                </Box>
              </Box>
            </Fade>
          )}
        </Paper>
      </Grow>
    </Box>
  );
};

export default Login;